import React from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../context/LanguageContext";
import { ChevronLeft, Trophy, Lock, Smartphone, Monitor } from "lucide-react";

/**
 * Admin/Mod-only placeholder preview for the upcoming World Cup mode.
 * Regular players are redirected back to V2 home.
 *
 * The full feature spec (48-player instances, 12 groups × 4 teams, Bo3 ties,
 * knockout brackets through Final, FIFA tiebreakers, screenshot result
 * submission, ghost penalties, 24h auto-loss, real-time standings, push
 * notifications) is intentionally NOT implemented yet. This page exists so
 * staff can iterate on copy, navigation, and the home-page banner UX before
 * launch is approved.
 */
export default function WorldCupPreviewV2() {
  const { platform } = useParams();
  const { user } = useAuth();
  const { t } = useT();
  const nav = useNavigate();

  const isStaff = user?.role === "admin" || user?.role === "moderator";
  if (!isStaff) return <Navigate to="/v2" replace />;

  const PlatformIcon = platform === "console" ? Monitor : Smartphone;
  const label = platform === "console" ? t("v2.wc.console.title") : t("v2.wc.mobile.title");

  return (
    <div className="px-5 pt-5 pb-6 space-y-4" data-testid="v2-worldcup-preview">
      <button
        type="button"
        onClick={() => nav(-1)}
        data-testid="v2-worldcup-back"
        className="inline-flex items-center gap-1.5 text-[#A1A1AA] hover:text-white text-xs font-mono uppercase tracking-[0.25em] transition-colors"
      >
        <ChevronLeft className="w-4 h-4" /> {t("v2.match.back")}
      </button>

      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl border border-white/10 shadow-2xl"
        style={{ background: `linear-gradient(135deg, ${platform === "console" ? "#059669" : "#7C3AED"} 0%, #1E3A8A 55%, #0F172A 100%)` }}
        data-testid="v2-worldcup-hero"
      >
        <div className="pointer-events-none absolute -top-10 -right-10 w-56 h-56 rounded-full bg-[#FFD700]/25 blur-3xl" />
        <div className="relative p-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm ring-1 ring-white/20 shadow-2xl mb-3">
            <Trophy className="w-9 h-9 text-[#FFD700]" />
          </div>
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#FFD700]">{t("v2.wc.kicker")}</div>
          <h1 className="text-3xl font-bold text-white mt-1 leading-tight">{label}</h1>
          <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FFD700] text-[#0F172A] text-[10px] font-bold uppercase tracking-widest shadow-lg">
            <Lock className="w-3 h-3" /> {t("v2.wc.soon")}
          </div>
          <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-white/70">
            <PlatformIcon className="w-3.5 h-3.5" /> {platform}
          </div>
        </div>
      </section>

      {/* What's coming */}
      <section className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-5 space-y-3" data-testid="v2-worldcup-spec">
        <h3 className="text-sm font-bold text-white">{t("v2.wc.spec.title")}</h3>
        <ul className="space-y-2 text-xs text-[#A1A1AA] leading-relaxed">
          <li><strong className="text-white">48 players</strong> — locked instance, mirrors the 2026 FIFA World Cup draw exactly</li>
          <li><strong className="text-white">12 groups of 4</strong> — Group A through Group L, randomly assigned national teams</li>
          <li><strong className="text-white">Best of 3 ties</strong> — win 2 of 3 games per fixture</li>
          <li><strong className="text-white">Top 2 + 8 best 3rd</strong> advance to Round of 32 → 16 → QF → SF → Final</li>
          <li>Same ePlay submission + screenshot + ghost-penalty rules</li>
          <li>Live standings, fixtures, feed, and bracket tabs</li>
        </ul>
      </section>

      {/* Staff-only note */}
      <section
        className="rounded-2xl bg-[#FFD700]/10 border border-[#FFD700]/30 p-4 text-xs text-[#FFD700] flex items-start gap-2"
        data-testid="v2-worldcup-staff-note"
      >
        <Lock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-bold uppercase tracking-widest text-[10px] mb-0.5">Staff preview</div>
          <p className="text-[#FFE99A] leading-relaxed">
            Only admins and mods can see this page. Regular players see the home-page banner with a "Coming Soon" lock — no link, no entry yet.
          </p>
        </div>
      </section>

      <Link
        to="/v2"
        data-testid="v2-worldcup-home-link"
        className="block w-full text-center rounded-2xl border border-[#1F1F2A] text-[#A1A1AA] font-bold text-xs py-3 hover:border-white hover:text-white transition-colors"
      >
        {t("v2.match.back")}
      </Link>
    </div>
  );
}
